import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

interface UsePrivatePublicationRedirectProps {
  isPrivate: boolean;
  requireSubscriberApproval: boolean;
  isLoggedIn: boolean;
  isStagingPreview: boolean;
  isFetchingSubscription: boolean;
}

/**
 * Hook to redirect users to home page for private publications on SPA navigation.
 * This is needed because clientLoader only runs on initial page load, not on SPA navigation.
 * For private publications, only the home page (/) is accessible - login uses ?modal=login.
 *
 * Note: For fully private publications (publication.private),
 * all routes except the home page and draft posts are blocked.
 * For publications with require_subscriber_approval, only post pages are gated.
 */

export const usePrivatePublicationRedirect = ({
  isPrivate,
  requireSubscriberApproval,
  isLoggedIn,
  isStagingPreview,
  isFetchingSubscription,
}: UsePrivatePublicationRedirectProps) => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (isFetchingSubscription) return;
    if (isLoggedIn || isStagingPreview) return;
    if (!isPrivate && !requireSubscriberApproval) return;

    const isHomePage = location.pathname === '/';
    const searchParams = new URLSearchParams(location.search);
    const isDraftPost = searchParams.get('draft') === 'true';

    if (isPrivate && !isHomePage && !isDraftPost) {
      navigate('/', { replace: true });
      return;
    }

    if (requireSubscriberApproval && location.pathname.startsWith('/p/') && !isDraftPost) {
      navigate('/', { replace: true });
    }
  }, [
    location.pathname,
    location.search,
    isPrivate,
    requireSubscriberApproval,
    isLoggedIn,
    isStagingPreview,
    isFetchingSubscription,
    navigate,
  ]);
};
